import {
  Card,
  Text,
  IndexTable,
  SkeletonBodyText,
  Box,
} from "@medentic/polaris-react";
import { useTranslation } from "react-i18next";
import type { Payment } from "@features/events/types";
import { Currency, RenderDate } from "@shared/ui";

export default function LatestTransactions({
  transactions,
  loading,
}: {
  transactions: Payment[];
  loading: boolean;
}) {
  const { t } = useTranslation();

  const resourceName = {
    singular: t("eventTransactions.payment"),
    plural: t("eventTransactions.payments"),
  };

  const headings = [
    { title: t("eventTransactions.date") },
    { title: t("eventTransactions.name") },
    { title: t("eventTransactions.event") },
    { title: t("eventTransactions.ticket") },
    { title: t("eventTransactions.amount"), alignment: "end" as const },
  ] as [
    { title: string; alignment?: "start" | "end" },
    ...{ title: string; alignment?: "start" | "end" }[]
  ];

  const skeletonRows = [1, 2, 3, 4, 5];
  const skeletonCells = [1, 2, 3, 4, 5];

  if (loading) {
    return (
      <Card padding="0">
        <IndexTable
          resourceName={resourceName}
          itemCount={skeletonRows.length}
          selectable={false}
          headings={headings}
        >
          {skeletonRows.map((rowId, index) => (
            <IndexTable.Row
              id={`latest-transactions-skeleton-row-${rowId}`}
              key={`latest-transactions-skeleton-row-${rowId}`}
              position={index}
            >
              {skeletonCells.map((cellId) => (
                <IndexTable.Cell
                  key={`latest-transactions-skeleton-cell-${rowId}-${cellId}`}
                >
                  <SkeletonBodyText lines={1} />
                </IndexTable.Cell>
              ))}
            </IndexTable.Row>
          ))}
        </IndexTable>
      </Card>
    );
  }

  const rowMarkup = transactions.map((transaction, index) => (
    <IndexTable.Row id={transaction.id} key={transaction.id} position={index}>
      <IndexTable.Cell>
        <RenderDate
          date={transaction.createdAt}
          isAgoFormat={true}
          reverseTooltip
        />
      </IndexTable.Cell>
      <IndexTable.Cell>
        <Text as="span" variant="bodySm">
          {transaction.payerName}
        </Text>
      </IndexTable.Cell>
      <IndexTable.Cell>
        <Box maxWidth="320px">
          <Text as="span" variant="bodySm" truncate>
            {transaction.eventTitle}
          </Text>
        </Box>
      </IndexTable.Cell>
      <IndexTable.Cell>
        <Text as="span" variant="bodySm">
          {transaction.assignedTickets && transaction.assignedTickets.length > 0
            ? [...new Set(transaction.assignedTickets.map((ticket) => ticket.title))].join(", ")
            : "-"}
        </Text>
      </IndexTable.Cell>
      <IndexTable.Cell>
        <Text as="span" variant="bodySm" alignment="end">
          <Currency value={transaction.amount} currency={transaction.currency} />
        </Text>
      </IndexTable.Cell>
    </IndexTable.Row>
  ));

  return (
    <Card padding="0">
      <IndexTable
        resourceName={resourceName}
        itemCount={transactions.length}
        selectable={false}
        headings={headings}
      >
        {rowMarkup}
      </IndexTable>
    </Card>
  );
}
